import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { PhotoAttribution } from '../components/PhotoAttribution';
import type { PhotoAttributionData } from '../components/PhotoAttribution';
import { t } from '../hooks/useT';

interface TopItem {
  id: string;
  puzzleNumber: number;
  likeCount: number;
  thumbUrl: string;
  publishDate: string;
  attribution: PhotoAttributionData | null;
  theme?: string;
}

export function TopDezoomPage() {
  const [items, setItems] = useState<TopItem[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api<{ items: TopItem[] }>('/gallery/top?limit=30')
      .then((data) => setItems(data.items))
      .catch((e) => setError(e.message));
  }, []);

  const creditEntries = useMemo(() => {
    const map = new Map<string, PhotoAttributionData>();
    for (const item of items) {
      const attr = item.attribution;
      if (!attr?.photographer || !attr.provider) continue;
      const key = `${attr.provider}:${attr.photographer}`;
      if (!map.has(key)) map.set(key, attr);
    }
    return [...map.values()].sort((a, b) => a.photographer.localeCompare(b.photographer));
  }, [items]);

  return (
    <main className="page gallery">
      <h1>{t('top.title')}</h1>
      <p className="sub">{t('top.sub')}</p>
      <Link to="/credits" className="link-muted">{t('gallery.credits')}</Link>

      {error && <p className="error">{error}</p>}

      <div className="gallery-grid">
        {items.map((item, index) => (
          <Link
            key={item.id}
            to={`/archives/${item.puzzleNumber}/play`}
            className="gallery-card gallery-card-link"
          >
            <span className="top-rank">#{index + 1}</span>
            <div className="gallery-card-thumb">
              <img src={item.thumbUrl} alt="" loading="lazy" />
              <PhotoAttribution attribution={item.attribution} compact />
            </div>
            <div className="gallery-card-meta">
              <span className="gallery-likes">♥ {item.likeCount}</span>
              <span className="top-puzzle-num">#{item.puzzleNumber}</span>
            </div>
          </Link>
        ))}
      </div>

      {!items.length && !error && <p className="muted">{t('top.empty')}</p>}

      {creditEntries.length > 0 && (
        <section className="top-credits">
          <h2 className="top-credits-title">{t('gallery.credits')}</h2>
          <ul className="top-credits-list">
            {creditEntries.map((attr) => (
              <li key={`${attr.provider}:${attr.photographer}`}>
                <PhotoAttribution attribution={attr} />
              </li>
            ))}
          </ul>
          <Link to="/credits" className="link-muted">{t('gallery.credits')}</Link>
        </section>
      )}

      <nav className="result-nav">
        <Link to="/play">{t('nav.daily')}</Link>
        <Link to="/archives">{t('archives.title')}</Link>
      </nav>
    </main>
  );
}
